/**
 * A Discord bot league's matches: each week's matchups (posted by the bot's /postschedule), your team for the
 * week, and the Ready button. When both players are ready, OgreClan's Showdown server (play.ogreclan.com) starts
 * the battle in the league's format, as soon as both are online there; results and replays come back here.
 */
import { useEffect, useState } from 'preact/hooks';
import { api, ApiError, type BotLeague, type BotMatchResult } from '../api';
import { OnlineDot } from '../league/schedule';
import { useMe } from '../me';
import { Link, navigate } from '../router';
import { ActionButton, ErrorBox, Loading, toast } from '../ui';

const PLAY = 'https://play.ogreclan.com';
const REPLAYS = 'https://replay.ogreclan.com';

export interface BotMatchesView {
	format: string | null;
	/** Admins' setting: league players get voice on play.ogreclan.com */
	voice: { on: boolean, room: string };
	currentWeek: number;
	you: number | null;
	/** Player ID -> their linked Showdown name, if any */
	showdown: Record<string, string | null>;
	/** Player ID -> online on play.ogreclan.com (for those with a linked account) */
	online: Record<string, boolean>;
	matches: {
		week: number, match: number, home: number, away: number, ready: number[], room: string | null,
		games: { room: string, winner: number | null, replay: string }[], winner: number | null, ended: boolean,
		/** Players who've saved a team for the week */
		teams: number[],
		/** What's been reported (by Ready, replay link or hand) */
		result: BotMatchResult | null,
	}[];
}

type Match = BotMatchesView['matches'][number];

function FormatSetting({ league, format, reload }: { league: BotLeague, format: string | null, reload: () => void }) {
	const [text, setText] = useState(format || '');
	useEffect(() => setText(format || ''), [format]);
	return <form class="row" onSubmit={async e => {
		e.preventDefault();
		try {
			const result = await api.put<{ name: string, bestOf: number | null, finished: number }>(`/api/bot-leagues/${league.id}/format`, { format: text });
			toast(`The league plays ${result.name} on play.ogreclan.com` +
				(result.bestOf ? `: a match is ${result.bestOf === 1 ? 'one game' : `best of ${result.bestOf}`}` : '') +
				(result.finished ? `, which finishes ${result.finished} match${result.finished === 1 ? '' : 'es'} already played.` : '.'), 'good');
			reload();
		} catch (error) {
			toast((error as Error).message, 'bad');
		}
	}}>
		<span class="small">Staff: the Showdown format</span>
		<input value={text} placeholder="gen9vgc2025regh, or a challenge code with @@@ rules" style="flex:1;min-width:220px"
			onInput={e => setText((e.target as HTMLInputElement).value)} />
		{/* saving the same format again is allowed: it sends the bot the best-of again */}
		<button type="submit" class="small" disabled={!text.trim()}>Save</button>
	</form>;
}

/** Admins: whether the league's players get voice on play.ogreclan.com, and in which division room */
function VoiceSetting({ league, voice, reload }: { league: BotLeague, voice: BotMatchesView['voice'], reload: () => void }) {
	const [room, setRoom] = useState(voice.room);
	useEffect(() => setRoom(voice.room), [voice.room]);
	const save = async (on: boolean) => {
		try {
			const result = await api.put<{ on: boolean, room: string }>(`/api/bot-leagues/${league.id}/voice`, { on, room });
			toast(result.on ? `Players get voice: global, and in ${result.room}. It takes a few seconds.` :
				`Turned off: players lose their voice in ${result.room} (global voice stays until they unlink).`, 'good');
			reload();
		} catch (error) {
			toast((error as Error).message, 'bad');
		}
	};
	return <div class="row" style="margin-top:6px">
		<label class="row small"><input type="checkbox" checked={voice.on} onChange={e => void save((e.target as HTMLInputElement).checked)} />
			Admin: give players voice on play.ogreclan.com</label>
		<span class="small muted">in the room</span>
		<input value={room} style="width:180px" onInput={e => setRoom((e.target as HTMLInputElement).value)} />
		{room !== voice.room && <button type="button" class="small" onClick={() => void save(voice.on)}>Save room</button>}
		<span class="muted small" style="width:100%">Players with a linked Showdown account get global voice (until they unlink) and voice in
			the division room (until the league is archived or deleted). Nobody's higher rank is changed.</span>
	</div>;
}

/** Admins: end the league: archive it, or delete it with everything in it */
function EndLeague({ league }: { league: BotLeague }) {
	if (['archived', 'cancelled'].includes(league.status)) return null;
	return <div class="row" style="margin-top:6px">
		<span class="small">Admin: end the league</span>
		<ActionButton class="small" confirm={`Archive ${league.name}? It stays readable, its channels are freed, and its players lose their division room voice.`}
			action={async () => {
				try {
					await api.post(`/api/bot-leagues/${league.id}/archive`);
					toast(`${league.name} is archived.`, 'good');
				} catch (error) {
					toast((error as Error).message, 'bad');
				}
			}}>Archive</ActionButton>
		<ActionButton class="small danger" action={async () => {
			const typed = window.prompt(`This deletes ${league.name} and everything in it (the bot keeps a backup). Type its name to delete it:`);
			if (typed === null) return;
			try {
				await api.post(`/api/bot-leagues/${league.id}/delete`, { confirm: typed });
				toast(`${league.name} is deleted.`, 'good');
				navigate('/league');
			} catch (error) {
				toast((error as Error).message, 'bad');
			}
		}}>Delete</ActionButton>
	</div>;
}

/** Your team for a week: pasted from the teambuilder, checked by Showdown against the format and your roster */
function TeamBox({ league, week, saved, onSaved }: { league: BotLeague, week: number, saved: boolean, onSaved: () => void }) {
	const [text, setText] = useState('');
	const [loaded, setLoaded] = useState('');
	const [problems, setProblems] = useState<string[]>([]);
	useEffect(() => {
		let stopped = false;
		api.get<{ team: string }>(`/api/bot-leagues/${league.id}/teams/${week}`).then(r => {
			if (stopped) return;
			setText(r.team);
			setLoaded(r.team);
		}, () => {});
		return () => {
			stopped = true;
		};
	}, [league.id, week]);
	return <div>
		<textarea rows={8} placeholder="Paste your team from the teambuilder (Export), or a pokepast.es link" value={text}
			onInput={e => setText((e.target as HTMLTextAreaElement).value)} />
		<div class="row" style="margin-top:6px">
			<ActionButton class="primary small" disabled={!text.trim() || text === loaded} action={async () => {
				try {
					const result = await api.put<{ team: string, pokepaste: string | null }>(`/api/bot-leagues/${league.id}/teams/${week}`, { team: text });
					// a Pokepaste link comes back as its team
					setText(result.team);
					setLoaded(result.team);
					setProblems([]);
					toast(`Saved your week ${week} team${result.pokepaste ? `, and your Pokepaste for the week` : ''}.`, 'good');
					onSaved();
				} catch (error) {
					setProblems(error instanceof ApiError && Array.isArray(error.data.problems) ? error.data.problems as string[] : []);
					toast((error as Error).message, 'bad');
				}
			}}>Save team</ActionButton>
			{saved && text === loaded && <span class="badge good">Saved</span>}
		</div>
		{problems.length > 0 && <ul class="notice bad small" style="margin:8px 0;padding-left:28px">{problems.map(p => <li key={p}>{p}</li>)}</ul>}
	</div>;
}

function YourMatch({ league, view, match, reload }: { league: BotLeague, view: BotMatchesView, match: Match, reload: () => void }) {
	const you = view.you!;
	const opponentId = match.home === you ? match.away : match.home;
	const name = (id: number) => league.pools.flatMap(p => p.players).find(p => p.id === id)?.name || 'Someone';
	const opponent = name(opponentId);
	const ready = match.ready.includes(you);
	const linked = !!view.showdown[you];
	const press = (value: boolean) => async () => {
		try {
			const result = await api.post<{ message: string }>(
				`/api/bot-leagues/${league.id}/matches/${match.week}/${match.match}/ready`, { ready: value });
			toast(result.message, 'good');
		} catch (error) {
			toast((error as Error).message, 'bad');
		}
		reload();
	};
	return <div class="card" style="outline:2px solid var(--accent)">
		<h3 style="margin-top:0">Your week {match.week} match: vs {opponent}</h3>
		<p class="row">
			{linked ? <OnlineDot online={view.online[you]} name="You" as={view.showdown[you]} /> : <span>You</span>}
			<span class="muted">·</span>
			{view.showdown[opponentId] ? <OnlineDot online={view.online[opponentId]} name={opponent} as={view.showdown[opponentId]} /> :
				<span>{opponent} <span class="muted small">(no Showdown account linked yet)</span></span>}
		</p>
		<Champions league={league} match={match} you={you} opponent={opponent} reload={reload} />
		{!league.website || match.result?.champions ? null :
		match.room ? <p class="notice good">It's being played: <a href={`${PLAY}/${match.room}`} target="_blank" rel="noreferrer">go to the battle</a>.</p> :
		match.ended ? null : <>
			{!view.format && <p class="notice">The league's staff haven't chosen its Showdown format yet.</p>}
			{!linked && <p class="notice">Link your Showdown account on <Link href="/account">your account page</Link>, so the battle knows who you are.</p>}
			<h4 style="margin:12px 0 4px">Your team for week {match.week}</h4>
			{view.format ? <TeamBox league={league} week={match.week} saved={match.teams.includes(you)} onSaved={reload} /> :
				<p class="muted small">You can save it once there's a format.</p>}
			<p style="margin-top:12px">{ready ? <strong>You're ready.</strong> : `You're not ready yet.`}{' '}
				{match.ready.includes(opponentId) ? `${opponent} is ready.` : `${opponent} isn't ready yet.`}</p>
			<div class="row">
				{ready ? <ActionButton action={press(false)}>Not ready</ActionButton> :
					<ActionButton class="primary" disabled={!match.teams.includes(you) || !linked} action={press(true)}>Ready!</ActionButton>}
			</div>
			<p class="muted small">When you're both ready and both online on <a href={PLAY} target="_blank" rel="noreferrer">play.ogreclan.com</a>, the
				battle starts there by itself with your saved teams. You can press Ready here and log in to Showdown afterwards.</p>
		</>}
		{match.result && match.result.games.length > 0 ? <ResultDetails league={league} result={match.result} /> :
			match.result && <Pastes league={league} result={match.result} />}
		<ReportGame league={league} match={match} you={you} opponentId={opponentId} reload={reload} />
	</div>;
}

const nameIn = (league: BotLeague, id: number | null) => league.pools.flatMap(p => p.players).find(p => p.id === id)?.name || 'Someone';

/** Playing the match on Pokémon Champions (both players agree), and your Pokepaste for the match */
function Champions({ league, match, you, opponent, reload }: {
	league: BotLeague, match: Match, you: number, opponent: string, reload: () => void,
}) {
	const [paste, setPaste] = useState('');
	const on = !!match.result?.champions;
	const asked = match.result?.asked.includes(you) ?? false;
	const mine = match.result?.pokepastes[String(you)];
	const post = async (path: string, body: unknown, done: string) => {
		try {
			await api.post(`/api/bot-leagues/${league.id}/matches/${match.week}/${path}`, body);
			toast(done, 'good');
			reload();
		} catch (error) {
			toast((error as Error).message, 'bad');
		}
	};
	return <div style="margin:8px 0">
		<label class="row small"><input type="checkbox" checked={asked} onChange={e => void post('champions', { on: (e.target as HTMLInputElement).checked },
			(e.target as HTMLInputElement).checked ? `You've asked to play on Champions.` : `You've taken it back.`)} />
			Play this match on Pokémon Champions
			{on ? <span class="badge good">both agreed</span> : asked ? <span class="muted">(waiting for {opponent} to agree)</span> : null}</label>
		<div class="row" style="margin-top:6px">
			<span class="small">Your Pokepaste{on ? '' : ' for this match'}</span>
			{mine && <a class="small" href={mine} target="_blank" rel="noreferrer">{mine}</a>}
			<input type="url" placeholder="https://pokepast.es/…" value={paste} onInput={e => setPaste((e.target as HTMLInputElement).value)} />
			<button type="button" class="small" disabled={!paste.trim()} onClick={() => void post('pokepaste', { url: paste.trim() }, 'Pokepaste saved.')}>Save</button>
		</div>
		<p class="small muted" style="margin:2px 0 0">{on ? `Needed for a match on Champions. ` : ''}Your opponent sees it once the match is over.
			A match played with Ready gets each team's Pokepaste by itself, if you haven't given one.</p>
	</div>;
}

/** Reporting a game: by its replay link (both sides), or your own Pokémon's kills and deaths by hand */
function ReportGame({ league, match, you, opponentId, reload }: {
	league: BotLeague, match: Match, you: number, opponentId: number, reload: () => void,
}) {
	const [open, setOpen] = useState(false);
	const [game, setGame] = useState((match.result?.games.length ?? 0) + 1);
	const [replay, setReplay] = useState('');
	const [winner, setWinner] = useState(0);
	const roster = league.pools.flatMap(p => p.players).find(p => p.id === you)?.roster.map(m => m.pokemon) ?? [];
	const [kd, setKd] = useState<Record<string, [string, string]>>({});
	const send = async (path: string, body: unknown) => {
		try {
			await api.post(`/api/bot-leagues/${league.id}/matches/${match.week}/${path}`, body);
			toast(`Game ${game} reported.`, 'good');
			setReplay('');
			setKd({});
			setWinner(0);
			setGame(game + 1);
			reload();
		} catch (error) {
			toast((error as Error).message, 'bad');
		}
	};
	if (!open) {
		return <p><button type="button" class="small" onClick={() => setOpen(true)}>Report a game</button>{' '}
			<span class="small muted">{league.website && !match.result?.champions ? `Games played with Ready are recorded by themselves.` :
				`Give each game's replay link, or your Pokémon's kills and deaths.`}</span></p>;
	}
	return <div class="card" style="margin-top:10px">
		<div class="row">
			<h4 style="margin:0">Report game</h4>
			<select value={game} onChange={e => setGame(Number((e.target as HTMLSelectElement).value))}>
				{[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
			</select>
			<button type="button" class="small" onClick={() => setOpen(false)}>Close</button>
		</div>
		{!match.result?.champions && <form class="row" style="margin-top:8px" onSubmit={e => {
			e.preventDefault();
			void send('replay', { game, replay: replay.trim() });
		}}>
			<input type="url" placeholder="Its replay: https://replay.ogreclan.com/…" value={replay} style="flex:1;min-width:220px"
				onInput={e => setReplay((e.target as HTMLInputElement).value)} />
			<button type="submit" class="small primary" disabled={!replay.trim()}>Report from the replay</button>
		</form>}
		<p class="small muted" style="margin:10px 0 4px">{match.result?.champions ? 'Your' : 'Or by hand: your'} Pokémon's kills and deaths in this game</p>
		<table style="width:auto"><tbody>{roster.map(mon => <tr key={mon}>
			<td>{mon}</td>
			{[0, 1].map(i => <td key={i}><input type="number" min="0" style="width:64px" placeholder={i ? 'D' : 'K'} value={kd[mon]?.[i] ?? ''}
				onInput={e => setKd({ ...kd, [mon]: Object.assign([...(kd[mon] || ['', ''])], { [i]: (e.target as HTMLInputElement).value }) as [string, string] })} /></td>)}
		</tr>)}</tbody></table>
		<div class="row" style="margin-top:6px">
			<select value={winner} onChange={e => setWinner(Number((e.target as HTMLSelectElement).value))}>
				<option value={0}>Who won? (either of you can say)</option>
				<option value={you}>I won</option>
				<option value={opponentId}>{nameIn(league, opponentId)} won</option>
			</select>
			<ActionButton class="small primary" disabled={!Object.values(kd).some(v => v[0] || v[1])} action={() => send('report', {
				game, winner: winner || null,
				kd: Object.entries(kd).filter(([, v]) => v[0] || v[1]).map(([pokemon, v]) => ({ pokemon, kills: Number(v[0] || 0), deaths: Number(v[1] || 0) })),
			})}>Report by hand</ActionButton>
		</div>
		<p class="small muted">Leave out Pokémon that didn't play. Your opponent reports their own side; staff fix mistakes.</p>
	</div>;
}

/** A match's games so far: who won each, and every Pokémon's kills and deaths */
function ResultDetails({ league, result, clear }: { league: BotLeague, result: BotMatchResult, clear?: (game: number) => Promise<void> }) {
	const sides = [...new Set(result.games.flatMap(g => g.stats.map(s => s.player)))];
	return <div style="margin-top:8px">
		{result.games.map(game => <div key={game.game} class="small" style="margin:6px 0">
			<strong>Game {game.game}</strong>: {game.winner ? `${nameIn(league, game.winner)} won` : 'winner not given'}
			{game.replay && <> · <a href={game.replay} target="_blank" rel="noreferrer">replay</a></>}
			<span class="muted"> · {game.source === 'battle' ? 'played with Ready' : game.source === 'replay' ? 'from its replay' : 'reported by hand'}</span>
			{clear && <> <ActionButton class="small" confirm={`Clear game ${game.game}? The players can report it again.`}
				action={() => clear(game.game)}>Clear</ActionButton></>}
			{sides.map(player => {
				const mons = game.stats.filter(s => s.player === player);
				return mons.length ? <div key={player}>{nameIn(league, player)}: {mons.map(m => `${m.pokemon} ${m.kills}-${m.deaths}`).join(', ')}</div> : null;
			})}
		</div>)}
		<Pastes league={league} result={result} />
	</div>;
}

/** The match's Pokepastes you can see: your own, and everyone's once it's over (staff always) */
function Pastes({ league, result }: { league: BotLeague, result: BotMatchResult }) {
	return <>{Object.entries(result.pokepastes).map(([player, url]) => <div key={player} class="small">
		{nameIn(league, Number(player))}'s Pokepaste: <a href={url} target="_blank" rel="noreferrer">{url}</a></div>)}</>;
}

/** A player's Pokepaste for a match, next to their name, if you can see it */
function PasteLink({ match, player }: { match: Match, player: number }) {
	const url = match.result?.pokepastes[String(player)];
	return url ? <> <a href={url} target="_blank" rel="noreferrer" title="Their Pokepaste for this match" class="small">📋</a></> : null;
}

/** Staff: report a game of any match from its replay, for the players (a game played without Ready) */
function StaffReplay({ league, week, matches, reload }: { league: BotLeague, week: number, matches: Match[], reload: () => void }) {
	const [match, setMatch] = useState(matches[0]?.match ?? 1);
	const [game, setGame] = useState(1);
	const [replay, setReplay] = useState('');
	return <form class="row small" style="margin-top:8px" onSubmit={async e => {
		e.preventDefault();
		try {
			await api.post(`/api/bot-leagues/${league.id}/matches/${week}/${match}/replay`, { game, replay: replay.trim() });
			toast(`Game ${game} reported from its replay.`, 'good');
			setReplay('');
			reload();
		} catch (error) {
			toast((error as Error).message, 'bad');
		}
	}}>
		<span>Staff: report from a replay</span>
		<select value={match} onChange={e => setMatch(Number((e.target as HTMLSelectElement).value))} aria-label="Match">
			{matches.map(m => <option key={m.match} value={m.match}>{nameIn(league, m.home)} vs {nameIn(league, m.away)}</option>)}
		</select>
		<select value={game} onChange={e => setGame(Number((e.target as HTMLSelectElement).value))} aria-label="Game">
			{[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>game {n}</option>)}
		</select>
		<input type="url" placeholder="https://replay.ogreclan.com/…" value={replay} style="flex:1;min-width:180px"
			onInput={e => setReplay((e.target as HTMLInputElement).value)} aria-label="Replay link" />
		<button type="submit" class="small" disabled={!replay.trim()}>Report</button>
	</form>;
}

/** Staff: post the next week (its matches, and approved trades into effect), like /postschedule */
function PostWeek({ league, currentWeek, reload }: { league: BotLeague, currentWeek: number, reload: () => void }) {
	const next = currentWeek + 1;
	return <div class="row" style="margin-top:6px">
		<span class="small">Staff: post the next week</span>
		<ActionButton class="small" confirm={`Post week ${next}? Its matches go up${league.siteOnly ? '' : ' on Discord'}, and approved trades take effect.`}
			action={async () => {
				try {
					const result = await api.post<{ matches: number, applied: number }>(`/api/bot-leagues/${league.id}/weeks`, { week: next });
					toast(`Week ${next} posted: ${result.matches} match${result.matches === 1 ? '' : 'es'}` +
						`${result.applied ? `, ${result.applied} trade${result.applied === 1 ? '' : 's'} now in effect` : ''}.`, 'good');
					reload();
				} catch (error) {
					toast((error as Error).message, 'bad');
				}
			}}>Post week {next}</ActionButton>
	</div>;
}

export function BotMatches({ league, reloadLeague }: { league: BotLeague, reloadLeague?: () => void }) {
	const me = useMe();
	const [view, setView] = useState<BotMatchesView | null>(null);
	const [error, setError] = useState<unknown>(null);
	const [loads, setLoads] = useState(0);
	const [yourWeek, setYourWeek] = useState(0);
	const [shown, setShown] = useState('');
	const reload = () => setLoads(n => n + 1);
	useEffect(() => {
		let stopped = false;
		let timer: ReturnType<typeof setTimeout>;
		const load = async () => {
			try {
				const loaded = await api.get<BotMatchesView>(`/api/bot-leagues/${league.id}/matches`);
				if (stopped) return;
				setView(loaded);
				setError(null);
			} catch (e) {
				if (!stopped) setError(e);
			}
			if (!stopped) timer = setTimeout(load, 10_000);
		};
		void load();
		return () => {
			stopped = true;
			clearTimeout(timer);
		};
	}, [league.id, loads]);

	if (error) return <ErrorBox error={error} />;
	if (!view) return <Loading />;
	const name = (id: number) => league.pools.flatMap(p => p.players).find(p => p.id === id)?.name || 'Someone';
	const weeks = [...new Set(view.matches.map(m => m.week))].sort((a, b) => a - b);
	const mine = view.you === null ? [] : view.matches.filter(m => m.week <= view.currentWeek && (m.home === view.you || m.away === view.you));
	const yours = mine.find(m => m.week === yourWeek) || mine.find(m => !m.ended && m.week === view.currentWeek) ||
		mine.find(m => !m.ended) || mine[mine.length - 1] || null;
	const staff = !!(me?.user?.isAdmin || me?.user?.isModerator || league.youHost);

	const status = (m: Match) => {
		const reported = m.result?.games.length ? m.result : null;
		if (reported && !m.room) {
			const won = (id: number) => reported.games.filter(g => g.winner === id).length;
			const key = `${m.week}-${m.match}`;
			return <a href="#" class="small" title={reported.champions ? 'Played on Pokémon Champions' : 'Kills and deaths'} onClick={e => {
				e.preventDefault();
				setShown(shown === key ? '' : key);
			}}><strong>{won(m.home)}–{won(m.away)}</strong>{reported.champions && ' (Champions)'}</a>;
		}
		if (m.ended) {
			return <span class="small">{m.winner !== null ? <strong>{name(m.winner)} won</strong> : 'Tie'}
				{m.games.map((g, i) => <> · <a key={g.room} href={`${REPLAYS}/${g.replay}`} target="_blank" rel="noreferrer">
					{m.games.length > 1 ? `game ${i + 1}` : 'replay'}</a></>)}</span>;
		}
		if (m.room) return <a class="badge good" href={`${PLAY}/${m.room}`} target="_blank" rel="noreferrer">Playing</a>;
		if (m.week > view.currentWeek) return <span class="muted small">vs</span>;
		return <span class="small muted">{m.ready.length ? `${m.ready.map(name).join(' and ')} ready` : 'vs'}</span>;
	};

	return <>
		<p class="muted">
			{view.format ? <>Matches are played on <a href={PLAY} target="_blank" rel="noreferrer">play.ogreclan.com</a> in <code>{view.format}</code>.</> :
				`The league's Showdown format hasn't been chosen yet.`}{' '}
			{view.currentWeek ? `Week ${view.currentWeek} is the latest posted.` : `No weeks have been posted yet (moderators post them with /postschedule).`}
		</p>
		{staff && <FormatSetting league={league} format={view.format} reload={reload} />}
		{staff && league.mode !== 'draft_only' && !['archived', 'cancelled'].includes(league.status) &&
			<PostWeek league={league} currentWeek={view.currentWeek} reload={() => {
				reload();
				reloadLeague?.();
			}} />}
		{me?.user?.isAdmin && <VoiceSetting league={league} voice={view.voice} reload={reload} />}
		{me?.user?.isAdmin && <EndLeague league={league} />}
		{mine.length > 1 && <div class="row small" style="margin-top:8px">Your match in
			<select value={yours?.week} onChange={e => setYourWeek(Number((e.target as HTMLSelectElement).value))}>
				{mine.map(m => <option key={m.week} value={m.week}>week {m.week}</option>)}
			</select></div>}
		{yours && <YourMatch key={yours.week} league={league} view={view} match={yours} reload={reload} />}
		{weeks.length ? <div class="grid" style="margin-top:12px">{weeks.map(week => <div key={week} class="card"
			style={week === view.currentWeek ? 'outline:2px solid var(--accent)' : ''}>
			<h3 style="margin-top:0">Week {week}{week === view.currentWeek && <span class="badge good"> this week</span>}</h3>
			<table><tbody>{view.matches.filter(m => m.week === week).map(m => <tr key={m.match}
				class={m.home === view.you || m.away === view.you ? 'you' : ''}>
				<td>{name(m.home)}{m.teams.includes(m.home) && !m.ended && <span title="Team saved"> ✓</span>}<PasteLink match={m} player={m.home} /></td>
				<td class="nowrap">{status(m)}</td>
				<td>{name(m.away)}{m.teams.includes(m.away) && !m.ended && <span title="Team saved"> ✓</span>}<PasteLink match={m} player={m.away} /></td>
			</tr>)}</tbody></table>
			{view.matches.filter(m => m.week === week && shown === `${m.week}-${m.match}` && m.result).map(m =>
				<ResultDetails key={m.match} league={league} result={m.result!} clear={staff ? async game => {
					try {
						await api.post(`/api/bot-leagues/${league.id}/matches/${m.week}/${m.match}/clear`, { game });
						toast(`Game ${game} cleared.`, 'good');
						reload();
					} catch (error) {
						toast((error as Error).message, 'bad');
					}
				} : undefined} />)}
			{staff && week <= view.currentWeek && <StaffReplay league={league} week={week} matches={view.matches.filter(m => m.week === week)} reload={reload} />}
		</div>)}</div> : <p class="empty">{league.docKind === 'template' ?
			`Weeks show here once they're posted with /postschedule (the schedule is on the spreadsheet).` : 'No schedule yet.'}</p>}
	</>;
}
